import { useCallback, useEffect, useState } from "react";

import CloseButton from "components/layout/CloseButton";
import FunctionalButton from "components/layout/FunctionalButton";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import { changePassword } from "lib/arcAuthApi";

interface ChangePasswordDialogProps {
  visible: boolean;
  username: string | null;
  isSelf: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const INPUT_CLASS =
  "w-full rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white placeholder:text-black60 focus:border-primary2 focus:outline-none";

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  visible,
  username,
  isSelf,
  onClose,
  onSaved,
}) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSaving(false);
      setError(null);
    }
  }, [visible]);

  const handleSubmit = useCallback(async () => {
    if (!username) return;
    if (isSelf && !currentPassword) {
      setError("Current password is required");
      return;
    }
    if (!newPassword) {
      setError("New password is required");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await changePassword(username, newPassword, isSelf ? currentPassword : undefined);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to change password");
    } finally {
      setSaving(false);
    }
  }, [username, isSelf, currentPassword, newPassword, confirmPassword, onSaved, onClose]);

  return (
    <AnimatedModal
      visible={Boolean(visible && username)}
      onClose={onClose}
      className="w-full max-w-sm rounded-r10 border border-black80 bg-black90 p-4"
      zIndexClassName="z-[10050]"
    >
      {username ? (
        <>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-h4 font-semibold text-white">
              Change Password — {username}
            </p>
            <CloseButton onClick={onClose} className="relative shrink-0" />
          </div>

          <div className="flex flex-col gap-3">
            {isSelf ? (
              <div className="flex flex-col gap-1.5">
                <label className="text-h5 text-white">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoFocus
                  autoComplete="current-password"
                  className={INPUT_CLASS}
                />
              </div>
            ) : null}
            <div className="flex flex-col gap-1.5">
              <label className="text-h5 text-white">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus={!isSelf}
                autoComplete="new-password"
                className={INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-h5 text-white">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                className={INPUT_CLASS}
              />
            </div>
          </div>

          {error ? (
            <p className="mt-3 text-h5 text-red-400" role="alert">
              {error}
            </p>
          ) : null}

          <div className="mt-4 flex items-center justify-end gap-2">
            <FunctionalButton
              className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80 disabled:opacity-50"
              title="Cancel"
              titleClassName="!normal-case text-h5 font-semibold text-white"
              onClick={onClose}
              disabled={saving}
            />
            <FunctionalButton
              className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 hover:bg-primary1 disabled:opacity-50"
              title={saving ? "Updating…" : "Update"}
              titleClassName="!normal-case text-h5 font-semibold text-white"
              onClick={() => void handleSubmit()}
              disabled={saving}
            />
          </div>
        </>
      ) : null}
    </AnimatedModal>
  );
};

export default ChangePasswordDialog;
